// D:\seat-reservation-react-v2\src\component\chatbot\GroupMembersOverlay.js
import React, { useState, useEffect } from "react";
import { Button, Alert, Form, Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaTrash,
  FaUserPlus,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import {
  getUsersForChat,
  removeGroupMember,
  addGroupMembers,
} from "../../services/chatService";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const GroupMembersOverlay = ({
  group,
  authUser,
  onClose,
  onMembersUpdated,
  // t = (key) => key,
}) => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupMembers, setGroupMembers] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [addMembersError, setAddMembersError] = useState("");
  const [localRemoveLoading, setLocalRemoveLoading] = useState(null);
  const [error, setError] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load group members
  useEffect(() => {
    const loadMembers = () => {
      if (group?.members && Array.isArray(group.members)) {
        // Get current group members
        const activeMembers = group.members
          .filter((member) => member.is_delete !== 1)
          .map((member) => ({
            ...member,
            id: member.user_id || member.id,
            user_id: member.user_id || member.id,
          }));
        setGroupMembers(activeMembers);
      }
    };

    loadMembers();
    fetchAllUsers();
  }, [group]);

  // Fetch ALL users - WITHOUT filtering by status
  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      setError("");
      // Get ALL users from API
      const response = await getUsersForChat();
      console.log("📋 Users API response:", response);

      if (response.success) {
        const usersList = response.data.users || [];
        // Debug: Show first few users
        usersList.slice(0, 5).forEach((user) => {
          console.log(`👤 Sample user:`, {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role,
            status: user.status, // Check if status exists
            hasStatus: "status" in user,
          });
        });

        setAllUsers(usersList);
      } else {
        setError(response.message || "Failed to load users");
      }
    } catch (error) {
      setError("Failed to load users: " + error.message);
    } finally {
      setLoadingUsers(false);
      setLoading(false);
    }
  };

  // Get available users (NOT in group, NOT current user)
  const getAvailableUsers = () => {
    if (!allUsers.length) return [];

    // Get IDs of users already in group
    const currentMemberIds = groupMembers.map((member) => member.id);

    // Filter users - REMOVE status check
    const available = allUsers.filter((user) => {
      const isInGroup = currentMemberIds.includes(user.id);
      const isCurrentUser = user.id === authUser?.id;

      // Remove status check - accept all active users
      // Since status might not be in API response
      const isActive = true; // Assume all users are active

      if (isInGroup) {
        console.log(`❌ Excluding ${user.id} (${user.name}): Already in group`);
        return false;
      }
      if (isCurrentUser) {
        console.log(`❌ Excluding ${user.id} (${user.name}): Current user`);
        return false;
      }

      // Accept user
      console.log(`✅ Including ${user.id} (${user.name})`);
      return true;
    });

    console.log("✅ Available users count:", available.length);
    available.forEach((user) => {
      console.log(
        `✅ Available: ${user.id} - ${user.name} (${user.email}) - ${user.role}`,
      );
    });

    return available;
  };

  // Available users (computed)
  const availableUsers = getAvailableUsers();

  // Handle member removal
  const handleRemoveMember = async (memberId, memberName) => {
    if (!group?.id || !memberId) return;

    // if (
    //   !window.confirm(
    //     `Are you sure you want to remove ${memberName} from the group?`,
    //   )
    // ) {
    //   return;
    // }

    try {
      setLocalRemoveLoading(memberId);
      setError("");

      const response = await removeGroupMember(group.id, memberId);

      if (response.success) {
        // Remove from local state
        toast.success(response.message);

        setGroupMembers((prev) =>
          prev.filter((member) => member.id !== memberId),
        );

        if (onMembersUpdated) {
          onMembersUpdated();
        }
      } else {
        setError(response.message || "Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      setError(error.message || "Failed to remove member");
    } finally {
      setLocalRemoveLoading(null);
    }
  };

  // Handle add members - SIMPLE VERSION LIKE GROUP CREATE
  const handleAddMembers = async () => {
    if (!group?.id || selectedUsersToAdd.length === 0) return;

    try {
      setAddingMembers(true);
      setAddMembersError("");
      setError("");

      console.log("➕ Adding users to group:", selectedUsersToAdd);

      // Use the SAME function as group creation
      const response = await addGroupMembers(group.id, selectedUsersToAdd);

      if (response.success) {
        console.log("✅ Add members response:", response);

        // Get added users from availableUsers
        const addedUsers = availableUsers
          .filter((user) => selectedUsersToAdd.includes(user.id))
          .map((user) => ({
            id: user.id,
            user_id: user.id,
            full_name: user.name,
            name: user.name,
            email: user.email,
            role: user.role,
            profile_image: user.avatar,
            member_role: "member",
            is_delete: 0,
          }));

        // Add to local state
        setGroupMembers((prev) => [...prev, ...addedUsers]);
        setSelectedUsersToAdd([]);
        setShowAddMembers(false);
        toast.success(response.message);

        if (onMembersUpdated) {
          onMembersUpdated();
        }
      } else {
        setAddMembersError(response.message || "Failed to add members");
      }
    } catch (error) {
      console.error("Error adding members:", error);
      setAddMembersError(error.message || "Failed to add members");
    } finally {
      setAddingMembers(false);
    }
  };

  // Check if current user is admin
  const isCurrentUserAdmin = () => {
    if (!group || !authUser) return false;

    if (group.created_by === authUser.id) return true;

    const userMember = group.members?.find((member) => {
      const memberId = member.user_id || member.id;
      return memberId === authUser.id && member.is_delete !== 1;
    });

    return userMember?.member_role === "admin" || userMember?.role === "admin";
  };

  const currentUserIsAdmin = isCurrentUserAdmin();

  if (loading) {
    return (
      <div className="group-members-overlay p-3" style={{ minHeight: "100vh" }}>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only p-0"></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group-members-overlay p-3"
      style={{ minHeight: "100vh", maxWidth: "600px" }}
    >
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          onClick={() => onClose && onClose()}
          className="p-0 me-3"
          style={{ fontSize: "20px" }}
        >
          <FaArrowLeft />
        </Button>
        <div>
          <h4 className="mb-1">
            {t("group")} {t("members")}
          </h4>
          <div className="text-muted">{group?.name}</div>
        </div>
      </div>

      {/* Add Members Button */}
      {currentUserIsAdmin && (
        <div className="mb-4">
          {!showAddMembers ? (
            <Button
              variant="primary"
              onClick={() => setShowAddMembers(true)}
              className="w-100 py-2"
            >
              <FaUserPlus className="me-2" /> {t("add")} {t("members")}
            </Button>
          ) : (
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    {t("add")} {t("members")}
                  </h5>
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => {
                      setShowAddMembers(false);
                      setSelectedUsersToAdd([]);
                    }}
                  >
                    <FaTimes size={20} />
                  </Button>
                </div>

                {loadingUsers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  </div>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="mb-3">
                      <FaUser size={48} className="text-muted" />
                    </div>
                    <p className="text-muted mb-2">{t("noUsersAvailable")}</p>
                    <p className="text-muted small mb-3">
                      {t("noUsersAvailableDesc")}
                    </p>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={fetchAllUsers}
                    >
                      {t("refresh")} {t("list")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      {/* <p className="text-muted mb-2">
                        {t("slelcet")} {t("slelcetuseradd ")} (
                        {availableUsers.length} {t("available")})
                      </p> */}
                      <div
                        className="users-list"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          padding: "10px",
                        }}
                      >
                        {availableUsers.map((user) => (
                          <div
                            key={user.id}
                            className="user-item py-2 border-bottom"
                          >
                            <Form.Check
                              type="checkbox"
                              id={`user-${user.id}`}
                              className="d-flex align-items-center gap-2"
                              label={
                                <div className="d-flex align-items-center">
                                  <div
                                    className="user-avatar me-3"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      backgroundColor: "#007bff",
                                      color: "white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "16px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="fw-bold mb-1">
                                      {user.name || `User ${user.id}`}
                                      <span className="badge bg-secondary ms-2">
                                        {user.role}
                                      </span>
                                    </div>
                                    <div className="text-muted small">
                                      {user.email}
                                    </div>
                                  </div>
                                </div>
                              }
                              checked={selectedUsersToAdd.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsersToAdd([
                                    ...selectedUsersToAdd,
                                    user.id,
                                  ]);
                                } else {
                                  setSelectedUsersToAdd(
                                    selectedUsersToAdd.filter(
                                      (id) => id !== user.id,
                                    ),
                                  );
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-primary">
                          {selectedUsersToAdd.length} {t("selected")}
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setShowAddMembers(false);
                            setSelectedUsersToAdd([]);
                          }}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleAddMembers}
                          disabled={
                            selectedUsersToAdd.length === 0 || addingMembers
                          }
                        >
                          {addingMembers ? (
                            <>
                              <div className="spinner-border" role="status">
                                <span className="sr-only p-0"></span>
                              </div>
                            </>
                          ) : (
                            <>
                              <FaUserPlus className="me-2" />
                              {t("add")} {t("selected")}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Current Members */}
      <div className="mb-4">
        <h5 className="mb-3">
          {t("current")} {t("members")} ({groupMembers.length + 1})
        </h5>
        <div className="members-list">
          {/* Current User */}
          <div className="member-item d-flex align-items-center justify-content-between p-3 bg-light rounded mb-2">
            <div className="d-flex align-items-center">
              <div
                className="avatar me-3"
                style={{
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  backgroundColor: "#28a745",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {authUser?.full_name?.charAt(0)?.toUpperCase() || "Y"}
              </div>
              <div>
                <div className="fw-bold">
                  {authUser?.full_name || t("you")}
                  {currentUserIsAdmin && (
                    <span className="badge bg-primary ms-2">{t("admin")}</span>
                  )}
                </div>
                <div className="text-muted small">{authUser?.email}</div>
              </div>
            </div>
            <span className="badge bg-success">{t("you")}</span>
          </div>

          {/* Other Members */}
          {groupMembers.map((member) => {
            const memberId = member.user_id || member.id;
            const memberName =
              member.full_name || member.name || `User ${memberId}`;
            const isAdmin =
              member.member_role === "admin" || member.role === "admin";
            const isCurrentUser = memberId === authUser?.id;

            if (isCurrentUser) return null;

            return (
              <div
                key={memberId}
                className="member-item d-flex align-items-center justify-content-between p-3 border rounded mb-2"
              >
                <div className="d-flex align-items-center">
                  <div
                    className="avatar me-3"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      backgroundColor: isAdmin ? "#dc3545" : "#007bff",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {memberName.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <div className="fw-bold">
                      {memberName}
                      {isAdmin && (
                        <span className="badge bg-danger ms-2">
                          {t("admin")}
                        </span>
                      )}
                    </div>
                    <div className="text-muted small">{member.email}</div>
                  </div>
                </div>
                {currentUserIsAdmin && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={localRemoveLoading === memberId}
                    onClick={() => handleRemoveMember(memberId, memberName)}
                  >
                    {localRemoveLoading === memberId ? (
                      <div className="spinner-border small" role="status">
                        <span className="sr-only p-0"></span>
                      </div>
                    ) : (
                      <>
                        <FaTrash />
                        {/* {t("remove")}  */}
                      </>
                    )}
                  </Button>
                )}
              </div>
            );
          })}

          {groupMembers.length === 0 && (
            <div className="text-center py-4 border rounded">
              <FaUser className="mb-2 text-muted" size={32} />
              <p className="text-muted">{t("noOtherMembers")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Debug Section */}
      {/* <div className='mt-4 p-3 border rounded bg-light'>
        <details>
          <summary className='fw-bold cursor-pointer'>
            {t("debug")} {t("information")}
          </summary>
          <div className='mt-2'>
            <p className='mb-1'>
              <strong>
                {t("total")} {t("users")}:
              </strong>{" "}
              {allUsers.length}
            </p>
            <p className='mb-1'>
              <strong>
                {t("group")} {t("members")}:
              </strong>{" "}
              {groupMembers.length}
            </p>
            <p className='mb-1'>
              <strong>
                {t("available")} {t("to")} {t("add")}:
              </strong>{" "}
              {availableUsers.length}
            </p>
            <p className='mb-2'>
              <strong>
                {t("group")} {t("ID")}:
              </strong>{" "}
              {group?.id}
            </p>
          </div>
        </details>
      </div> */}
    </div>
  );
};

export default GroupMembersOverlay;
